import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import { EmptyBoxIcon, RefreshIcon, XCircleIcon } from '@/components/icons';
import { formatShortDateTime } from '@/utils/date';
import type { Task } from '@/types/task';
import { TaskProgressBar, TaskStatusIndicator, TaskTypeBadge } from './TaskStatus';

/**
 * Row actions of the tasks table
 */
export type TaskRowAction = 'cancel' | 'retry';

export interface TasksTableProps {
  tasks: Task[];
  isLoading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onSelect: (task: Task) => void;
  getActions: (task: Task) => Record<TaskRowAction, boolean>;
  isBusy: (action: TaskRowAction, taskId: string) => boolean;
  onAction: (action: TaskRowAction, task: Task) => void;
}

const COLUMN_COUNT = 6;

/**
 * Background tasks table; clicking a row opens its details
 */
export function TasksTable({
  tasks,
  isLoading,
  hasActiveFilters,
  onClearFilters,
  onSelect,
  getActions,
  isBusy,
  onAction,
}: TasksTableProps) {
  return (
    <Table isLoading={isLoading} skeletonColumns={COLUMN_COUNT} skeletonRows={5}>
      <TableHead>
        <TableRow>
          <TableHeader>Type</TableHeader>
          <TableHeader>Description</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Progress</TableHeader>
          <TableHeader>Created</TableHeader>
          <TableHeader align="right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {tasks.length === 0 && !isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <TableEmpty
                icon={<EmptyBoxIcon />}
                message={hasActiveFilters ? 'No tasks match your filters' : 'No tasks yet'}
                action={
                  hasActiveFilters ? (
                    <Button variant="secondary" size="sm" onClick={onClearFilters}>
                      Clear filters
                    </Button>
                  ) : undefined
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          tasks.map((task) => {
            const actions = getActions(task);

            return (
              <TableRow key={task.id} clickable onClick={() => onSelect(task)}>
                <TableCell>
                  <TaskTypeBadge type={task.type} />
                </TableCell>
                <TableCell>
                  <span className="tasks-description" title={task.description}>
                    {task.description || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <TaskStatusIndicator status={task.status} />
                </TableCell>
                <TableCell>
                  <TaskProgressBar task={task} />
                </TableCell>
                <TableCell>
                  <span className="tasks-date">{formatShortDateTime(task.created_at)}</span>
                </TableCell>
                <TableCell align="right">
                  <div className="tasks-actions" onClick={(e) => e.stopPropagation()}>
                    {actions.cancel && (
                      <IconButton
                        className="tasks-action-btn tasks-action-btn-danger"
                        icon={<XCircleIcon />}
                        label="Cancel task"
                        isBusy={isBusy('cancel', task.id)}
                        onClick={() => onAction('cancel', task)}
                      />
                    )}
                    {actions.retry && (
                      <IconButton
                        className="tasks-action-btn tasks-action-btn-retry"
                        icon={<RefreshIcon />}
                        label="Retry failed subtasks"
                        isBusy={isBusy('retry', task.id)}
                        onClick={() => onAction('retry', task)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
